import React, { useEffect, useMemo, useRef, useState } from 'react';
import mermaid from 'mermaid';

// ---------- Config ----------
const MERMAID_INIT = {
  startOnLoad: false,
  securityLevel: 'loose',
  theme: 'base',
  themeVariables: {
    primaryColor: '#10b981', // Green for nodes
    primaryTextColor: '#f8fafc', // Light text
    primaryBorderColor: '#10b981',
    lineColor: '#10b981', // Edge color
    secondaryColor: '#4b5563', // Darker gray for secondary nodes
    tertiaryColor: '#9ca3af', // Lighter gray for formulas
    fontFamily: 'Inter, Arial, sans-serif',
    fontSize: '14px', // Slightly larger for readability
    background: '#ffffff', // White background
    nodeBorder: '#1f2937', // Dark border
    clusterBkg: '#f1f5f9', // Light gray for clusters
    clusterBorder: '#e5e7eb', // Subtle cluster border
  },
  flowchart: {
    useMaxWidth: true,
    htmlLabels: true,
    curve: 'basis', // Smooth edges
    nodeSpacing: 30,
    rankSpacing: 50,
    padding: 15,
  },
  mindmap: {
    useMaxWidth: true,
    padding: 20, // Tighter padding for cleaner look
    maxNodeWidth: 180, // Smaller nodes for better fit
    nodeFontSize: '14px',
    nodeFontWeight: '500', // Medium weight for clarity
    spacingFactor: 1.8, // Tighter spacing for compact mindmaps
  },
  sequence: {
    useMaxWidth: true,
    actorFontSize: '14px',
    messageFontSize: '12px',
  },
  er: {
    useMaxWidth: true,
    entityPadding: 20,
  },
};

// ---------- Example Diagrams ----------
const EXAMPLES = {
  mindmap: `mindmap
  root((Main Topic))
    Branch A
      Leaf 1
      Leaf 2
    Branch B
      Leaf 3
      Leaf 4`,
  flowchart: `graph TD
    A[Start] --> B{Decision}
    B -->|Yes| C[Action 1]
    B -->|No| D[Action 2]
    C --> E[End]
    D --> E
    style A fill:#10b981,stroke:#1f2937,stroke-width:2px
    style B fill:#4b5563,stroke:#1f2937,stroke-width:2px
    style C fill:#10b981,stroke:#1f2937,stroke-width:2px
    style D fill:#10b981,stroke:#1f2937,stroke-width:2px
    style E fill:#9ca3af,stroke:#1f2937,stroke-width:2px`,
};

// ---------- Utils ----------
const stripZeroWidth = (s) => s.replace(/[\u200B-\u200D\uFEFF]/g, '');
const unescapeNewlines = (s) => s.replace(/\\r\\n|\\n|\\r/g, '\n');
const stripMdFencesAnywhere = (s) =>
  s.replace(/```[a-zA-Z0-9_-]*/g, '').replace(/```/g, '');
const stripDisableRun = (s) => s.replace(/:disable-run/gi, '');
const normalizeLineEndings = (s) => s.replace(/\r/g, '');
const trimBlankEdges = (s) => s.trim();

const tryExtractFromJSON = (raw) => {
  try {
    const txt = raw.trim();
    if (!txt.startsWith('{') && !txt.startsWith('[')) return raw;
    const obj = JSON.parse(txt);
    const candidates = ['mermaid_code', 'mermaid', 'diagram', 'diagram_code', 'code', 'mindmap_code'];
    for (const key of candidates) {
      if (typeof obj[key] === 'string' && obj[key].trim()) return obj[key];
    }
    return raw;
  } catch {
    return raw;
  }
};

const ultraRobustMindmapFix = (code) => {
  if (!code || typeof code !== 'string') return 'mindmap\n  root((Empty Diagram))';
  
  let lines = code.split('\n').filter(line => line.trim().length > 0);
  if (lines.length === 0) return 'mindmap\n  root((Empty Diagram))';

  const allNodes = lines
    .filter(line => !line.toLowerCase().includes('mindmap'))
    .map(line => line.trim().replace(/^root\(/i, '').replace(/\)$/, '').replace(/\(\(/g, '(').replace(/\)\)/g, ')'))
    .filter(content => content.length > 0);

  if (allNodes.length === 0) return 'mindmap\n  root((Empty Diagram))';

  let rootContent = 'Main Topic';
  const rootCandidates = allNodes.filter(node => 
    node.toLowerCase().includes('root') || 
    node.match(/\([^)]+\)/) ||
    node.length > 3
  );

  if (rootCandidates.length > 0) {
    rootContent = rootCandidates[0].replace(/^root\s*/i, '').replace(/\(/g, '').replace(/\)/g, '').trim();
    if (rootContent.length === 0) rootContent = 'Main Topic';
  }

  const result = ['mindmap', `  root((${rootContent}))`];
  const childNodes = allNodes.filter((node, index) => {
    const cleanNode = node.replace(/^root\s*/i, '').replace(/\(/g, '').replace(/\)/g, '').trim();
    return cleanNode !== rootContent && cleanNode.length > 0;
  });

  childNodes.forEach((node, index) => {
    const cleanNode = node.replace(/^root\s*/i, '').replace(/\(/g, '').replace(/\)/g, '').trim();
    if (cleanNode && cleanNode !== rootContent) {
      result.push(`    ${cleanNode}`);
    }
  });

  if (childNodes.length === 0) {
    result.push('    Example Child Node');
  }

  return result.join('\n');
};

const createFallbackMindmap = (originalCode) => {
  const words = originalCode
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 2 && !['mindmap', 'root'].includes(word.toLowerCase()))
    .slice(0, 8);

  if (words.length === 0) {
    return `mindmap
  root((Your Content))
    Add your mindmap content here
      Use proper indentation
      Each level should be indented by 2 spaces
    Example Structure
      Main Topic
        Subtopic 1
        Subtopic 2`;
  }

  const rootWord = words[0] || 'Content';
  const children = words.slice(1);
  const result = [`mindmap`, `  root((${rootWord}))`];
  children.forEach((child, index) => {
    result.push(`    ${child}`);
  });

  return result.join('\n');
};

const bulletproofSanitizeByType = (code, type) => {
  try {
    let s = code ?? '';
    s = tryExtractFromJSON(s);
    s = stripZeroWidth(s);
    s = unescapeNewlines(s);
    s = stripMdFencesAnywhere(s);
    s = stripDisableRun(s);
    s = normalizeLineEndings(s);
    s = trimBlankEdges(s);

    if (type === 'mindmap') {
      try {
        let fixed = ultraRobustMindmapFix(s);
        const lines = fixed.split('\n');
        const hasMindmap = lines.some(line => line.toLowerCase().includes('mindmap'));
        const hasRoot = lines.some(line => line.toLowerCase().includes('root('));
        if (!hasMindmap || !hasRoot) {
          throw new Error('Basic structure validation failed');
        }
        return fixed;
      } catch (fixError) {
        console.warn('Mindmap fix failed, using fallback:', fixError);
        return createFallbackMindmap(s);
      }
    }

    return s
      .split('\n')
      .map((ln) => ln.replace(/\t/g, '  '))
      .join('\n')
      .replace(/[ \t]+$/gm, '')
      .replace(/^\s*\n+/, '')
      .replace(/\n{4,}/g, '\n\n');
  } catch (error) {
    console.error('Sanitization failed, returning default:', error);
    return `mindmap
  root((Your Diagram))
    The content could not be parsed
    Please check your syntax
    Ensure proper indentation`;
  }
};

const detectType = (raw) => {
  try {
    const s = baseSanitize(raw).toLowerCase();
    if (s.startsWith('mindmap')) return 'mindmap';
    if (/\bflowchart\b|\bgraph (?:tb|td|lr|rl|bt)\b/.test(s)) return 'flowchart';
    if (/\berdiagram\b/.test(s)) return 'er';
    if (/\bclassdiagram\b/.test(s)) return 'class';
    if (/\bsequencediagram\b/.test(s)) return 'sequence';
    if (/\bgantt\b/.test(s)) return 'gantt';
    if (/\bstatediagram\b|\bstatediagram-v2\b/.test(s)) return 'state';
    return 'flowchart';
  } catch {
    return 'flowchart';
  }
};

const baseSanitize = (raw) => {
  let s = raw ?? '';
  s = tryExtractFromJSON(s);
  s = stripZeroWidth(s);
  s = unescapeNewlines(s);
  s = stripMdFencesAnywhere(s);
  s = stripDisableRun(s);
  s = normalizeLineEndings(s);
  s = trimBlankEdges(s);
  return s;
};

let globalCounter = 0;
const nextId = () => `mermaid-svg-${Date.now()}-${++globalCounter}`;

// ---------- Component ----------
const MermaidRenderer = ({
  code,
  type = 'auto',
  theme = 'base',
  className = '',
  diagramCode,
  mindmapCode,
  diagramType,
  style,
}) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [svg, setSvg] = useState('');
  const rawInput = code ?? mindmapCode ?? diagramCode ?? '';
  const resolvedType = useMemo(
    () => (type === 'auto' || !type ? detectType(rawInput) : type || diagramType || 'flowchart'),
    [type, diagramType, rawInput]
  );

  const cleaned = useMemo(() => bulletproofSanitizeByType(rawInput, resolvedType), [rawInput, resolvedType]);

  useEffect(() => {
    console.log('MermaidRenderer - Cleaned code:', cleaned);
    console.log('MermaidRenderer - Resolved type:', resolvedType);

    setError(null);
    setSvg('');

    try {
      mermaid.initialize({ ...MERMAID_INIT, theme });
    } catch (e) {
      setError(`Failed to initialize Mermaid: ${e.message}`);
      return;
    }

    let safeCode = cleaned;
    if (!safeCode || safeCode.length < 3) {
      setError(`No valid diagram code provided. Using ${resolvedType} example.`);
      safeCode = EXAMPLES[resolvedType] || EXAMPLES.flowchart;
    }

    const id = nextId();
    let cancelled = false;

    (async () => {
      try {
        const renderPromise = mermaid.render(id, safeCode);
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Mermaid rendering timed out after 8 seconds')), 8000);
        });

        const { svg: out } = await Promise.race([renderPromise, timeoutPromise]);

        if (!cancelled) {
          setSvg(out);
          if (containerRef.current) {
            const svgElement = containerRef.current.querySelector('svg');
            if (svgElement) {
              svgElement.style.width = '100%';
              svgElement.style.height = 'auto';
              svgElement.setAttribute('aria-label', `${resolvedType} Diagram`);
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          const syntheticMindmap = createFallbackMindmap(rawInput);
          try {
            const { svg: syntheticSvg } = await mermaid.render(`synthetic-${id}`, syntheticMindmap);
            setSvg(syntheticSvg);
          } catch (syntheticError) {
            setError(`Failed to render diagram: ${e.message}\n\nPlease check your syntax or try a simpler structure.`);
            setSvg('');
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cleaned, theme, resolvedType, rawInput]);

  return (
    <div className={`w-full max-w-4xl mx-auto ${className}`} style={{ ...style, overflow: 'auto' }}>
      {error && (
        <div className="rounded-lg p-4 mb-4 border border-red-500/30 bg-red-500/10">
          <div className="font-semibold mb-2 text-red-400 flex items-center">
            <span className="mr-2">⚠️</span> Diagram Rendering Error
          </div>
          <pre className="whitespace-pre-wrap text-sm text-red-300">{error}</pre>
          <details className="mt-2 text-red-300">
            <summary className="cursor-pointer hover:text-red-200">Show cleaned code</summary>
            <pre className="whitespace-pre-wrap text-xs mt-2 text-red-200 bg-red-500/5 p-2 rounded">{cleaned}</pre>
          </details>
          <button
            className="mt-2 px-3 py-1 bg-red-500/20 text-red-200 rounded hover:bg-red-500/30"
            onClick={() => {
              setError(null);
              setSvg('');
              const id = nextId();
              mermaid.render(id, cleaned).then(({ svg }) => {
                setSvg(svg);
                if (containerRef.current) {
                  const svgElement = containerRef.current.querySelector('svg');
                  if (svgElement) {
                    svgElement.style.width = '100%';
                    svgElement.style.height = 'auto';
                  }
                }
              }).catch(() => {
                setError('Retry failed. Please check the diagram code.');
              });
            }}
          >
            Retry Rendering
          </button>
        </div>
      )}

      {svg ? (
        <div
          className={`w-full mermaid-container animate-fade-in ${className}`}
          style={{ width: '100%', height: 'auto', overflow: 'visible' }}
          ref={containerRef}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : !error ? (
        <div className="text-sm text-gray-500 flex items-center">
          <span className="mr-2">📊</span> Rendering {resolvedType} diagram…
        </div>
      ) : null}
    </div>
  );
};

// ---------- CSS for Animation ----------


export default MermaidRenderer;